import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Shield, MessageSquare, ChevronRight, Loader2 } from 'lucide-react';
import ChangePasswordForm from '../UpdatePassword';
import TwoFactorAuth from '../TwoFactorAuth';
import Communication from '../Communication';

const Settings: React.FC = () => {
  const SafeAnimatePresence = AnimatePresence as any;
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'password' | '2fa' | 'communication'>('password');

  useEffect(() => {
    const loadData = async () => {
      await new Promise(resolve => setTimeout(resolve, 800)); 
      setLoading(false); 
    };
    loadData();
  }, []);

  const sections = [
    { 
      id: 'password', 
      title: 'Security', 
      subtitle: 'Manage your password and account protection', 
      icon: <Lock size={20} />, 
      component: <ChangePasswordForm /> 
    },
    { 
      id: '2fa', 
      title: 'Two-Factor Auth', 
      subtitle: 'Add an extra layer of security', 
      icon: <Shield size={20} />, 
      component: <TwoFactorAuth /> 
    },
    { 
      id: 'communication', 
      title: 'Communication', 
      subtitle: 'Notifications and contact preferences', 
      icon: <MessageSquare size={20} />, 
      component: <Communication /> 
    },
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <Loader2 className="w-10 h-10 text-primary animate-spin" />
        <p className="mt-4 text-slate-500 font-medium">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8">
      <header className="mb-10">
        <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Account Settings</h1>
        <p className="text-slate-500 font-medium">Manage your profile, security, and preferences.</p>
      </header>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Navigation Sidebar */}
        <div className="w-full lg:w-72 space-y-2">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id as any)}
              className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all duration-200 ${
                activeSection === section.id 
                  ? "bg-white shadow-lg shadow-primary/5 border-primary/20 border text-primary" 
                  : "text-slate-500 hover:bg-slate-100/50"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${activeSection === section.id ? "bg-primary text-white" : "bg-slate-100 text-slate-400"}`}>
                  {section.icon}
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold leading-none mb-1">{section.title}</p>
                  <p className="text-[10px] opacity-70 uppercase tracking-widest font-bold">Manage</p>
                </div>
              </div>
              {activeSection === section.id && <ChevronRight size={16} />}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-grow">
          <SafeAnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-[2.5rem] p-8 md:p-12 border border-slate-100 shadow-sm min-h-[400px]"
            >
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  {sections.find(s => s.id === activeSection)?.title}
                </h3>
                <p className="text-slate-500 font-medium italic">
                  {sections.find(s => s.id === activeSection)?.subtitle}
                </p>
                <div className="h-px bg-slate-100 w-full mt-6" />
              </div>

              <div className="settings-content-wrapper">
                {sections.find(s => s.id === activeSection)?.component}
              </div>
            </motion.div>
          </SafeAnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Settings;
